import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Combobox,
  ComboboxContent,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
  ComboboxValue,
  useComboboxAnchor,
} from '../../components/ui/combobox';
import { Button } from '../../components/ui/button';
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { useDealClients } from './useDealDetails';
import { getRupee } from '../../utils/numbers';
import { useAnalytics } from '../../hooks/useAnalytics';

export interface Client {
  clientPublicId: string;
  name: string;
  pan: string;
  balance: number;
  family: any;
  familyName?: string;
  isKycVerified?: boolean;
  goldInGms?: number;
  silverInGms?: number;
}

type ClientItem = {
  code: string;
  value: Client;
  label: string;
  balance: string;
};

interface ClientListWithSearchProps {
  client: Client | null;
  setClient: (client: Client | null) => void;
  showGold?: boolean;
  showSilver?: boolean;
  refetchClients?: boolean;
  onChange?: () => void;
}

const SEARCH_DEBOUNCE_MS = 400;

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

function clientToItem(c: Client, showGold: boolean, showSilver: boolean): ClientItem {
  return {
    code: c.clientPublicId,
    value: c,
    label: c.name,
    balance: showGold
      ? `${c.goldInGms ?? 0} gm`
      : showSilver
        ? `${c.silverInGms ?? 0} gm`
        : getRupee.format(c.balance),
  };
}

export function ClientListWithSearch({
  client,
  setClient,
  showGold = false,
  showSilver = false,
  refetchClients = false,
  onChange,
}: ClientListWithSearchProps) {
  const [searchInput, setSearchInput] = useState('');
  const [open, setOpen] = useState(false);
  const debouncedSearch = useDebounce(searchInput, SEARCH_DEBOUNCE_MS);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const popupContentRef = useRef<HTMLDivElement>(null);
  const anchorRef = useComboboxAnchor();

  const isMetalSip = showGold || showSilver;
  const { data: dealClients, isLoading, refetch } = useDealClients(0, debouncedSearch, isMetalSip);
  const { track } = useAnalytics();

  const clients = dealClients?.data?.items ?? [];

  const items = useMemo(
    () =>
      clients
        .filter((c: Client) => c?.isKycVerified)
        .map((c: Client) => clientToItem(c, showGold, showSilver)),
    [clients, showGold, showSilver],
  );

  const selectedItem = useMemo(
    () => (client ? clientToItem(client, showGold, showSilver) : null),
    [client, showGold, showSilver],
  );

  useEffect(() => {
    if (refetchClients) refetch();
  }, [refetchClients, refetch]);

  const handleValueChange = useCallback(
    (value: ClientItem | null) => {
      setClient(value?.value ?? null);
      setOpen(false);
      setSearchInput('');
      onChange?.();
    },
    [setClient, onChange],
  );

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
      if (nextOpen) {
        track({ eventName: 'Client_Dropdown_Opened' });
      } else {
        setSearchInput('');
      }
    },
    [track],
  );

  //   const isInitialLoad = isLoading && items.length === 0 && !searchInput;

  useEffect(() => {
    if (!open) return;
    requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
  }, [items, open]);

  // Keep focus in search input when Base UI moves it to trigger or list
  useEffect(() => {
    if (!open) return;

    const handleFocusIn = () => {
      requestAnimationFrame(() => {
        if (!open) return;
        const active = document.activeElement as HTMLElement | null;
        if (!active) return;
        const isTrigger =
          anchorRef.current?.contains(active) && active.closest('[data-slot="combobox-trigger"]');
        const isList = active.closest('[data-slot="combobox-list"]');
        if (!isTrigger && !isList) return;
        const input =
          popupContentRef.current?.querySelector('input') ??
          document.querySelector('[data-slot="combobox-content"] input');
        if (input instanceof HTMLInputElement) input.focus();
      });
    };

    document.addEventListener('focusin', handleFocusIn);
    return () => document.removeEventListener('focusin', handleFocusIn);
  }, [open, anchorRef]);

  return (
    <div className="w-full min-w-0">
      <Combobox<ClientItem>
        value={selectedItem}
        inputValue={searchInput}
        open={open}
        onOpenChange={handleOpenChange}
        onInputValueChange={setSearchInput}
        onValueChange={handleValueChange}
        items={items}
        filteredItems={items}
        filter={() => true}
        itemToStringLabel={(item) => item?.label ?? ''}
        isItemEqualToValue={(a, b) => a?.code === b?.code}
      >
        {/*  Anchor wrapper - group enables hover/active on child trigger */}
        <div ref={anchorRef} className="w-full group/trigger">
          <ComboboxTrigger
            className="w-full block rounded-lg border border-stone-300 bg-white transition-colors hover:bg-stone-100 active:bg-stone-200 data-[popup-open]:bg-stone-100"
            render={
              <Button
                tabIndex={-1}
                variant="outline"
                className="!flex w-full flex-row items-center justify-between gap-2 font-normal h-10 rounded-lg border-0 bg-transparent shadow-none hover:bg-transparent text-left"
              >
                <span className="min-w-0 flex-1 truncate text-left">
                  <ComboboxValue placeholder={'Select client'} />
                </span>
                <span className="ml-auto flex-shrink-0">
                  <ChevronDownIcon
                    className={`text-muted-foreground size-4 pointer-events-none ${open ? 'hidden' : 'block'}`}
                  />
                  <ChevronUpIcon
                    className={`text-muted-foreground size-4 pointer-events-none ${open ? 'block' : 'hidden'}`}
                  />
                </span>
              </Button>
            }
          />
        </div>

        <ComboboxContent
          side="bottom"
          anchor={anchorRef}
          className="bg-white w-[var(--anchor-width)] min-w-[var(--anchor-width)] p-2.5"
        >
          <div ref={popupContentRef}>
            <ComboboxInput
              showTrigger={false}
              placeholder="Search client by first name, PAN"
              className="bg-white w-full border border-stone-200"
              ref={searchInputRef}
            />

            <div className="flex px-4 justify-between border-b border-stone-200 py-2">
              <h4 className="text-xs font-medium text-stone-700">Name of Client</h4>
              <p className="text-xs font-medium text-stone-700">
                {showGold ? 'Gold' : showSilver ? 'Silver' : 'Wallet'} Balance
              </p>
            </div>

            {isLoading ? (
              <div className="max-h-80 overflow-auto bg-white flex items-center justify-center py-8">
                <p className="text-sm text-stone-500">Fetching clients...</p>
              </div>
            ) : (
              <>
                {items.length === 0 ? (
                  <div className="flex w-full items-center justify-center py-8 text-center">
                    <p className="text-sm text-stone-500">
                      {debouncedSearch.trim() ? 'No client found' : 'No clients found'}
                    </p>
                  </div>
                ) : (
                  <ComboboxList className="max-h-80 overflow-auto bg-white">
                    {(item: ClientItem) => (
                      <ComboboxItem key={item.code} value={item} tabIndex={-1}>
                        <div className="flex justify-between items-center w-full gap-3 cursor-pointer">
                          <div className="flex flex-col min-w-0">
                            <span className="text-sm font-medium text-slate-600 truncate">
                              {item.label}
                            </span>
                            <span className="text-xs text-slate-500">{item.value.pan}</span>
                          </div>

                          {item.value.familyName && (
                            <span className="text-xs font-medium text-green-800 bg-green-800/15 rounded-lg px-2 py-1 shrink-0">
                              {item.value.familyName}
                            </span>
                          )}

                          <span className="text-sm font-medium text-slate-600 shrink-0">
                            {item.balance}
                          </span>
                        </div>
                      </ComboboxItem>
                    )}
                  </ComboboxList>
                )}
              </>
            )}
          </div>
        </ComboboxContent>
      </Combobox>
    </div>
  );
}
